import React from 'react';
import styled from 'styled-components';
import ProductCard from '@99designs/apps/spa/common/Product/ProductCard';
import { useProductsQuery } from '@99designs/apps/spa/pages/create/common/Products.generated';
import { ButtonWrapper } from '@99designs/apps/spa/pages/create/components/Button';
import ContentSetting from '@99designs/apps/spa/pages/create/components/ContentSetting';
import { isFixedPriceFragment } from '@99designs/apps/spa/pages/create/util/filterProducts';
import { Box, Button, Heading, Link, PageHeading, Stack, Text } from '@99designs/emu';
import { LoadableElement } from '@99designs/frontend-components/components/LoadableElement';
import { FormattedReactMessage } from '@99designs/frontend-components/i18n';
import { CurrencyCode } from '@99designs/i18n';
import { linkTo } from '@99designs/routing';
import { ViewProductDetails } from '@99designs/spa-common';
import { packageToProductSummary } from '@99designs/spa-common';
import { Price } from '@99designs/spa-common';

const CardContainer = styled.div`
    > * {
        background-color: white;
    }
`;

export default function EssentialsProductSelection({
    category,
    fromPrice,
}: {
    category: string;
    fromPrice: number;
}) {
    const { error, data, loading } = useProductsQuery({
        variables: { input: { categories: [category] } },
    });

    if (error) {
        throw error;
    }

    if (loading || !data) {
        return <LoadableElement variations={['fade-in']} isLoading={true} />;
    }

    return (
        <ContentSetting>
            <Box marginTop="1.5bu" marginBottom="1.5bu">
                <PageHeading>{__('Get started with the essentials')}</PageHeading>
            </Box>
            <Box marginTop={['none', '2bu']} position="relative">
                <Stack space="1.5bu">
                    {data.products.map((p) => {
                        if (!isFixedPriceFragment(p)) {
                            return null;
                        }

                        const packageForProductSummary = {
                            title: p.title,
                            description: p.description,
                            amountInCents: p.pricing.amount.amountInCents,
                            currency: p.pricing.amount.currency,
                        };

                        return (
                            <CardContainer key={p.key}>
                                <ProductCard
                                    rightColumn={
                                        <Stack space="1bu">
                                            <Box display="flex" justifyContent="center">
                                                <ViewProductDetails
                                                    features={p.details}
                                                    additionalDetails={p.additionalDetails}
                                                    productSummary={packageToProductSummary({
                                                        ...packageForProductSummary,
                                                    })}
                                                    image={p.image}
                                                    headerVideo={p.headerVideo}
                                                />
                                            </Box>
                                            <ButtonWrapper>
                                                <Button
                                                    size="large"
                                                    appearance="primary"
                                                    width="fill"
                                                    href={linkTo.briefCreate(p.key)}
                                                    type="button"
                                                >
                                                    {__('Start brief')}
                                                </Button>
                                            </ButtonWrapper>
                                        </Stack>
                                    }
                                    productSummary={packageToProductSummary(
                                        packageForProductSummary
                                    )}
                                    features={p.details}
                                    hasLeftBorder={false}
                                />
                            </CardContainer>
                        );
                    })}
                </Stack>
            </Box>
            <Box marginTop="3bu" maxWidth="620px">
                <Stack space="0.75bu">
                    <Heading appearanceLevel={5} accessibilityLevel={5}>
                        {__('Need something other than the essentials?')}
                    </Heading>
                    <div>
                        <FormattedReactMessage
                            message={__(
                                'Require extra revisions or maybe a short turnaround? <Link>Get tailored quotes from up to 5 designers</Link>, based on your specific needs.'
                            )}
                            replacements={{
                                Link: <Link href={linkTo.projectsBrief({ category })} />,
                            }}
                        />
                        &nbsp;
                        <Text weight="bold">
                            <FormattedReactMessage
                                message={__('From <Price>price</Price>')}
                                replacements={{
                                    Price: <Price amount={fromPrice} currency={CurrencyCode.USD} />,
                                }}
                            />
                        </Text>
                    </div>
                </Stack>
            </Box>
        </ContentSetting>
    );
}
